import IndividualCoefficientsTooltip from "./IndividualCoefficientsTooltip";

import { mode as appMode } from "../../../../../../app/mode";

import IndividualCoefficientsService, {
    MOTIVATION_MODE,
    COEFFICIENT_MODE,
} from "../../../../../../frontend.services/LeaderRequests/IndividualCoefficientsService";

import { CLEAR_INDIVIDUAL_COEFFICIENTS } from "../../../../../../redux/enums/Request/individualCoefficients.types";
import {
    createOrGetDraftThunkAction,
} from "../../../../../../redux/actions/Requests/RequestsFromLeader/IndividualCoefficients/createOrGetDraftThunkAction";
import {
    getAvailableEventsThunkAction,
} from "../../../../../../redux/actions/Requests/RequestsFromLeader/IndividualCoefficients/getAvailableEventsThunkAction";
import {
    getIndividualCoefficientThunkAction,
} from "../../../../../../redux/actions/Requests/RequestsFromLeader/IndividualCoefficients/getIndividualCoefficientThunkAction";
import {
    updateIndividualCoefficientThunkAction,
} from "../../../../../../redux/actions/Requests/RequestsFromLeader/IndividualCoefficients/updateIndividualCoefficientThunkAction";
import {
    validateIndividualCoefficientThunkAction,
} from "../../../../../../redux/actions/Requests/RequestsFromLeader/IndividualCoefficients/validateIndividualCoefficientThunkAction";

import DropdownMenu from "../../../../../common/Menu/DropdownMenu/DropdownMenu";
import CustomButton from "../../../../../common/Button/CustomButton/CustomButton";
import RequestButtons from "../../../../../common/Button/RequestButtons/RequestButtons";
import NotificationWindowRequests from "../../../../../common/NotificationWindowRequests/NotificationWindowRequests";
import { Modal } from "../../../../../common/Modal/Modal";
import ModalBanOverlay from "../../../../../common/Modal/ModalBanOverlay/ModalBanOverlay";
import BackHandlerModal from "../../../../../common/Modal/ModalConditions/Requests/BackHandlerModal/BackHandlerModal";
import SaveSuccessModal from "../../../../../common/Modal/ModalConditions/Requests/SaveSuccessModal/SaveSuccessModal";
import RequestVerification
    from "../../../../../common/Modal/ModalConditions/Requests/RequestVerification/RequestVerification";
import Error500Modal from "../../../../../common/Modal/ModalConditions/Requests/Error500Modal/Error500Modal";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";

import "./IndividualCoefficients.scss";

const AUTOSAVE_DELAY = 800;

function formatNumber(value) {
    if (value === null || value === undefined || Number.isNaN(value)) return "";
    return new Intl.NumberFormat("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

const IndividualCoefficients = ({ requestStatus, setRequestStatus }) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const individualCoefficients = useSelector(state => state.requests.individualCoefficients);
    const uid = individualCoefficients?.uid;
    const version = individualCoefficients?.version;
    const eventId = individualCoefficients?.eventId;
    const eventName = individualCoefficients?.eventName;
    const requestTypeId = individualCoefficients?.requestTypeId ?? 22;

    // название типа заявки по requestTypeId — как в остальных заявках руководителя:
    // из справочника state.requests.categories (type === 3). Фолбэк — на случай,
    // если справочник не загружен (заход по прямой ссылке на страницу заявки)
    const requestTypeName = useSelector(state =>
        state.requests.categories
            ?.find(category => category.type === 3)?.types
            ?.find(type => type.id === requestTypeId)?.name,
    ) || "Установление индивидуальных коэффициентов";

    const [isLoading, setIsLoading] = useState(true);
    const [events, setEvents] = useState([]);
    const [eventsUnavailableMessage, setEventsUnavailableMessage] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [mode, setMode] = useState(MOTIVATION_MODE);
    const [rows, setRows] = useState([]);
    const [isDirty, setIsDirty] = useState(false);
    const [rowErrors, setRowErrors] = useState({});
    const [totalsErrors, setTotalsErrors] = useState([]);

    const [sendButtonLoading, setSendButtonLoading] = useState(false);
    const [modalStatus, setModalStatus] = useState(false);
    const [modalSuccessStatus, setModalSuccessStatus] = useState(false);
    const [modalSentStatus, setModalSentStatus] = useState(false);
    const [onValidation, setOnValidation] = useState(false);
    const [modalError, setModalError] = useState({
        sign: false,
        validation: false,
        status: false,
        saving: false,
    });

    const saveTimeoutRef = useRef(null);

    const totals = useMemo(() => IndividualCoefficientsService.calculateTotals(rows), [rows]);
    const totalsInvalid = useMemo(() => IndividualCoefficientsService.isTotalsInvalid(totals), [totals]);
    const hasUnfilledRows = useMemo(() => IndividualCoefficientsService.hasUnfilledRows(rows), [rows]);

    async function loadAvailableEvents() {
        const result = await dispatch(getAvailableEventsThunkAction());
        if (!result.ok) {
            // это транспортная/серверная ошибка, а не бизнес-ответ "мероприятий нет" —
            // показываем как ошибку загрузки с повтором, а не как eventsUnavailableMessage
            setEvents([]);
            setEventsUnavailableMessage(null);
            setLoadError({ message: result.error.message, source: "events" });
            return false;
        }
        setLoadError(null);
        if (result.data && result.data.isAvailable) {
            setEvents(result.data.events || []);
            setEventsUnavailableMessage(null);
            return true;
        }
        setEvents([]);
        setEventsUnavailableMessage(result.data?.message || "Создание заявки недоступно");
        return false;
    }

    async function loadDraft(eventIdParam) {
        setIsLoading(true);
        setLoadError(null);
        const result = await dispatch(createOrGetDraftThunkAction({ eventId: eventIdParam }));
        if (result.ok) {
            setRows(result.data.rows || []);
            setIsDirty(false);
            setRowErrors({});
            setTotalsErrors([]);
            setIsLoading(false);
            return;
        }
        if (result.error.kind === "eventUnavailable") {
            setRows([]);
            setRequestStatus({
                isSuccess: false,
                details: [{
                    block: 1,
                    message: result.error.message ||
                        "Выбранное мероприятие больше недоступно. Обновите список мероприятий.",
                }],
            });
            await loadAvailableEvents();
        } else {
            // NotificationWindowRequests рендерится только рядом с таблицей, а её при неудачной
            // загрузке черновика нет — поэтому ошибку показываем отдельным блоком с повтором
            setRows([]);
            setLoadError({ message: result.error.message, eventId: eventIdParam, source: "draft" });
        }
        setIsLoading(false);
    }

    async function retryLoadHandler() {
        if (loadError?.source === "events") {
            setIsLoading(true);
            await loadAvailableEvents();
            setIsLoading(false);
            return;
        }
        await loadDraft(loadError?.eventId);
    }

    useEffect(() => {
        (async () => {
            await loadAvailableEvents();
            setIsLoading(false);
        })();
        return () => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
            dispatch({ type: CLEAR_INDIVIDUAL_COEFFICIENTS });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function reloadAfterConflict() {
        const result = await dispatch(getIndividualCoefficientThunkAction(uid));
        setIsDirty(false);
        if (result.ok) {
            setRows(result.data.rows || []);
            setRequestStatus({
                isSuccess: false,
                details: [{ block: 1, message: "Заявка была изменена, данные обновлены. Проверьте и повторите ввод." }],
            });
        } else {
            setRequestStatus({ isSuccess: false, details: [{ block: 1, message: result.error.message }] });
        }
    }

    async function reloadAfterNotDraft() {
        const result = await dispatch(getIndividualCoefficientThunkAction(uid));
        setIsDirty(false);
        if (result.ok) {
            setRows(result.data.rows || []);
            setRequestStatus({
                isSuccess: false,
                details: [{
                    block: 1,
                    message: `Заявка больше не в статусе черновика (текущий статус: ${result.data.status}). ` +
                        "Данные обновлены.",
                }],
            });
        } else {
            setRequestStatus({ isSuccess: false, details: [{ block: 1, message: result.error.message }] });
        }
    }

    function handleWriteError(error) {
        if (error.kind === "versionConflict") {
            return reloadAfterConflict();
        }
        if (error.kind === "notDraft") {
            return reloadAfterNotDraft();
        }
        return setRequestStatus({ isSuccess: false, details: [{ block: 1, message: error.message }] });
    }

    async function runAutosave(rowsToSave) {
        // страховка: не отправляем на бэк неполные/пустые значения строк —
        // на motivationAmount === null сохранение падает
        if (IndividualCoefficientsService.hasUnfilledRows(rowsToSave)) return;
        const result = await dispatch(updateIndividualCoefficientThunkAction({ uid, version, rows: rowsToSave }));
        if (result.ok) {
            // не перезаписываем локальные rows ответом автосейва: version/uid уже обновлены в сторе
            // (дёрнется GET_INDIVIDUAL_COEFFICIENTS_DRAFT внутри тханка), а сами значения строк
            // локально посчитаны той же формулой, что и на бэке — иначе контролируемый инпут теряет
            // курсор посреди набора, а таблица дёргается из-за пересчёта тултипов/подсветки
            setRowErrors({});
            setTotalsErrors([]);
            setIsDirty(false);
        } else if (result.error.kind === "validation") {
            const parsed = IndividualCoefficientsService.parseProblemJsonErrors(result.error.errors);
            setRowErrors(parsed.rowErrors);
            setTotalsErrors(parsed.totalsErrors);
        } else {
            await handleWriteError(result.error);
        }
    }

    function scheduleAutosave(nextRows) {
        setIsDirty(true);
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => {
            runAutosave(nextRows);
        }, AUTOSAVE_DELAY);
    }

    // ограничиваем количество цифр целой части, иначе Number(value) на сверхдлинной строке
    // даёт Infinity, и расчётные суммы/коэффициент разваливаются в NaN/Infinity:
    // коэффициент по бизнес-правилу лежит в диапазоне 0–1.5 — хватает 1 цифры до точки;
    // для мотивации в рублях явного бизнес-максимума нет, берём разумный потолок в 9 цифр
    // (до 999 999 999,99 ₽) — с запасом, но без возможности набрать "бесконечность"
    const COEFFICIENT_INPUT_PATTERN = /^-?\d{0,1}\.?\d{0,2}$/;
    const MOTIVATION_INPUT_PATTERN = /^-?\d{0,9}\.?\d{0,2}$/;

    function rowChangeHandler(rowIndex, rawValue) {
        const normalized = rawValue.replace(",", ".");
        const pattern = mode === COEFFICIENT_MODE ? COEFFICIENT_INPUT_PATTERN : MOTIVATION_INPUT_PATTERN;
        // допускаем промежуточные состояния набора вроде "1." или "-" —
        // если сразу привести к Number, точка "теряется" и число вида 1.2 набрать невозможно
        if (!pattern.test(normalized)) return;

        const isIncomplete = normalized === "" || normalized === "-" || normalized.endsWith(".");

        setRows((prev) => {
            const next = [...prev];
            const field = mode === COEFFICIENT_MODE ? "individualCoefficient" : "motivationAmount";
            const editedRow = { ...next[rowIndex], [field]: normalized };
            next[rowIndex] = isIncomplete ? editedRow : IndividualCoefficientsService.recalculateRow(editedRow, mode);
            if (isIncomplete) {
                // промежуточный/пустой ввод не отправляем на автосейв: бэк падает на null,
                // а значение ещё не дописано — сохранимся на следующем валидном символе или по blur
                setIsDirty(true);
                if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
            } else {
                scheduleAutosave(next);
            }
            return next;
        });
    }

    function rowBlurHandler(rowIndex) {
        setRows((prev) => {
            const field = mode === COEFFICIENT_MODE ? "individualCoefficient" : "motivationAmount";
            const rawValue = prev[rowIndex][field];
            if (rawValue === "" || rawValue === null || rawValue === undefined || rawValue === "-") {
                return prev;
            }

            const next = [...prev];
            const normalizedNumber = mode === COEFFICIENT_MODE
                ? IndividualCoefficientsService.roundCoefficient(rawValue)
                : IndividualCoefficientsService.roundMoney(rawValue);
            const editedRow = { ...next[rowIndex], [field]: normalizedNumber };
            next[rowIndex] = IndividualCoefficientsService.recalculateRow(editedRow, mode);
            scheduleAutosave(next);
            return next;
        });
    }

    function eventChangeHandler(event, element) {
        if (element.eventId === eventId) return;
        loadDraft(element.eventId);
    }

    function backHandler() {
        if (isDirty) {
            setModalStatus(true);
        } else {
            navigate("/main/hr-services/requests/leader");
        }
    }

    async function saveHandler() {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        setModalStatus(false);
        const result = await dispatch(updateIndividualCoefficientThunkAction({ uid, version, rows }));
        if (result.ok) {
            setRows(result.data.rows || []);
            setIsDirty(false);
            setModalSuccessStatus(true);
        } else if (result.error.kind === "versionConflict") {
            await reloadAfterConflict();
        } else if (result.error.kind === "notDraft") {
            await reloadAfterNotDraft();
        } else {
            setModalError((prev) => ({ ...prev, status: true, saving: true }));
        }
    }

    async function submitHandler() {
        if (hasUnfilledRows) {
            setRequestStatus({
                isSuccess: false,
                details: [{ block: 1, message: "Заполните мотивацию и коэффициент для всех сотрудников" }],
            });
            return;
        }

        setSendButtonLoading(true);
        const validation = await dispatch(validateIndividualCoefficientThunkAction({ uid, version }));
        setSendButtonLoading(false);

        if (validation.ok) {
            // подпись и сам POST /submit — внутри RequestVerification (ввод пароля ЛК)
            setModalSentStatus(true);
            return;
        }

        if (validation.error.kind === "versionConflict") {
            await reloadAfterConflict();
        } else if (validation.error.kind === "notDraft") {
            await reloadAfterNotDraft();
        } else if (validation.error.kind === "businessValidation") {
            setRequestStatus({
                isSuccess: false,
                details: IndividualCoefficientsService.toNotificationDetails(validation.error.details),
            });
        } else if (validation.error.kind === "validation") {
            const parsed = IndividualCoefficientsService.parseProblemJsonErrors(validation.error.errors);
            setRowErrors(parsed.rowErrors);
            setTotalsErrors(parsed.totalsErrors);
            setRequestStatus({ isSuccess: false, details: [{ block: 1, message: "Проверьте заполненные данные." }] });
        } else {
            setRequestStatus({ isSuccess: false, details: [{ block: 1, message: validation.error.message }] });
        }
    }

    function closeErrorModal(e) {
        e.stopPropagation();
        setModalError({ sign: false, validation: false, status: false, saving: false });
    }

    return (
        <>
            {/*{*/}
            {/*    appMode === "PROD" ? (*/}
            {/*        <>*/}
            {/*            <div className={"sub-header"}>Заявки руководителя</div>*/}
            {/*            <div style={{*/}
            {/*                margin: "60px auto",*/}
            {/*                fontSize: "24px",*/}
            {/*                lineHeight: "32px",*/}
            {/*                color: "gray",*/}
            {/*                width: "1160px",*/}
            {/*                minHeight: "300px",*/}
            {/*            }}>*/}
            {/*                На данный момент заявка находится в разработке*/}
            {/*            </div>*/}
            {/*        </>*/}
            {/*    ) : (*/}
            <div className={"individual-coefficients"}>
                <div className={"sub-header individual-coefficients__header"}>
                    {/*<div className={"sub-header individual-coefficients__header__item"}>*/}
                    <div>

                                Установление индивидуальных коэффициентов
                        <IndividualCoefficientsTooltip
                            content={"Раздел для установления индивидуальных коэффициентов премирования " +
                                            "сотрудникам по итогам выбранного мероприятия оценки."}
                        />
                    </div>


                    {/*</div>*/}
                    <div className={"individual-coefficients__event-row"}>
                        <DropdownMenu
                            styleName={"individual-coefficients-event"}
                            listOfOptions={events}
                            fieldId={"eventId"}
                            fieldName={"eventName"}
                            stateProperty={"eventId"}
                            initialMenuMessage={eventName || "Выберите мероприятие"}
                            selectOptionClick={eventChangeHandler}
                        />
                    </div>

                </div>

                {
                    isLoading ? (
                        <div className={"individual-coefficients__loader"}>
                            <ClipLoader color={"#6DCDF6"} />
                        </div>
                    ) : loadError ? (
                        <div className={"individual-coefficients__unavailable"}>
                            <div className={"individual-coefficients__load-error"}>
                                <div>{loadError.message}</div>
                                <CustomButton
                                    function={retryLoadHandler}
                                    title={"Повторить"}
                                    regular={true}
                                />
                            </div>
                        </div>
                    ) : eventsUnavailableMessage ? (
                        <div className={"individual-coefficients__unavailable"}>
                            {eventsUnavailableMessage}
                        </div>
                    ) : !eventId ? (
                        <div className={"individual-coefficients__unavailable"}>
                                    Выберите мероприятие, чтобы продолжить
                        </div>
                    ) : (
                        <>
                            <div className={"individual-coefficients__toolbar"}>
                                <div className={"individual-coefficients__mode-toggle"}>
                                    <div
                                        onClick={() => setMode(MOTIVATION_MODE)}
                                        className={
                                            mode === MOTIVATION_MODE
                                                ? "individual-coefficients__mode-toggle__item" +
                                                        " individual-coefficients__mode-toggle__item--active"
                                                : "individual-coefficients__mode-toggle__item"
                                        }
                                    >
                                        <span className={"individual-coefficients__mode-toggle__item__icon"}>
                                                    ₽
                                        </span>
                                        <div>
                                            <div className={"individual-coefficients__mode-toggle__item__title"}>
                                                        Сумма мотивации
                                            </div>
                                            <div className={"individual-coefficients__mode-toggle__item__subtitle"}>
                                                        Редактируется поле «Мотивация»
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        onClick={() => setMode(COEFFICIENT_MODE)}
                                        className={
                                            mode === COEFFICIENT_MODE
                                                ? "individual-coefficients__mode-toggle__item" +
                                                        " individual-coefficients__mode-toggle__item--active"
                                                : "individual-coefficients__mode-toggle__item"
                                        }
                                    >
                                        <span className={"individual-coefficients__mode-toggle__item__icon"}>
                                                    KI
                                        </span>
                                        <div>
                                            <div className={"individual-coefficients__mode-toggle__item__title"}>
                                                        Коэффициент
                                            </div>
                                            <div className={"individual-coefficients__mode-toggle__item__subtitle"}>
                                                        Редактируется поле «Коэффициент (KI)»
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={"individual-coefficients__mode-info"}>
                                    <span className={"individual-coefficients__mode-info__icon"}>i</span>
                                    <span>
                                                При выборе режима редактируется только выделенное поле.
                                                Другое поле рассчитывается автоматически.
                                    </span>
                                </div>
                            </div>



                            <table className={"individual-coefficients__table"}>
                                {/* table-layout: fixed берёт ширины отсюда — без этого браузер
                                        пересчитывает колонки по содержимому на каждое нажатие клавиши,
                                        и таблица "дёргается" по горизонтали */}
                                <colgroup>
                                    <col style={{ width: "7%" }} />
                                    <col style={{ width: "18%" }} />
                                    <col style={{ width: "19%" }} />
                                    <col style={{ width: "13%" }} />
                                    <col style={{ width: "12%" }} />
                                    <col style={{ width: "15%" }} />
                                    <col style={{ width: "12%" }} />
                                    <col style={{ width: "4%" }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>Таб. №</th>
                                        <th>Должность</th>
                                        <th>ФИО работника</th>
                                        <th>Базовая сумма премии (ПКВ), ₽</th>
                                        <th>Мотивация (+/- в ₽)</th>
                                        <th>Расчетная сумма к начислению, ₽</th>
                                        <th>Коэффициент (KI)</th>
                                        <th className={"individual-coefficients__table__status"}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, index) => {
                                        const outOfRange = IndividualCoefficientsService
                                            .isCoefficientOutOfRange(row.individualCoefficient);
                                        const rowExceedsBase = totalsInvalid &&
                                                    IndividualCoefficientsService.isRowExceedingBase(row);
                                        const rowStatusMessages = [
                                            rowExceedsBase &&
                                                        "Суммарная расчетная сумма не должна быть больше суммарной базовой суммы",
                                            outOfRange &&
                                                        "Коэффициент индивидуальной оценки должен находиться " +
                                                        "в диапазоне от 0 до 1,5.",
                                        ].filter(Boolean);
                                        return (
                                            <tr key={row.prid}>
                                                <td>{row.personnelNumber}</td>
                                                <td>{row.positionName}</td>
                                                <td>{row.fullName}</td>
                                                <td className={"individual-coefficients__table__cell--center"}>
                                                    {formatNumber(row.baseBonusAmount)}
                                                </td>
                                                <td className={"individual-coefficients__table__cell--center"}>
                                                    {
                                                        mode === MOTIVATION_MODE ? (
                                                            <input
                                                                className={
                                                                    "individual-coefficients__table__input" +
                                                                            (rowStatusMessages.length > 0
                                                                                ? " individual-coefficients__table__input--error"
                                                                                : "")
                                                                }
                                                                value={row.motivationAmount ?? ""}
                                                                onChange={
                                                                    (e) => rowChangeHandler(index, e.target.value)
                                                                }
                                                                onBlur={() => rowBlurHandler(index)}
                                                            />
                                                        ) : (
                                                            <span
                                                                className={"individual-coefficients__table__readonly"}
                                                            >
                                                                {formatNumber(row.motivationAmount)}
                                                            </span>
                                                        )
                                                    }
                                                    {
                                                        rowErrors[index]?.motivationAmount && (
                                                            <div
                                                                className={"individual-coefficients__table__error"}
                                                            >
                                                                {rowErrors[index].motivationAmount}
                                                            </div>
                                                        )
                                                    }
                                                </td>
                                                <td className={"individual-coefficients__table__cell--center"}>
                                                    {formatNumber(row.calculatedAmount)}
                                                </td>
                                                <td className={"individual-coefficients__table__cell--center"}>
                                                    {
                                                        mode === COEFFICIENT_MODE ? (
                                                            <input
                                                                className={
                                                                    "individual-coefficients__table__input" +
                                                                            (rowStatusMessages.length > 0
                                                                                ? " individual-coefficients__table__input--error"
                                                                                : "")
                                                                }
                                                                value={row.individualCoefficient ?? ""}
                                                                onChange={
                                                                    (e) => rowChangeHandler(index, e.target.value)
                                                                }
                                                                onBlur={() => rowBlurHandler(index)}
                                                            />
                                                        ) : (
                                                            <span
                                                                className={"individual-coefficients__table__readonly"}
                                                            >
                                                                {formatNumber(row.individualCoefficient)}
                                                            </span>
                                                        )
                                                    }
                                                </td>
                                                <td className={"individual-coefficients__table__status"}>
                                                    {
                                                        rowStatusMessages.length > 0 && (
                                                            <IndividualCoefficientsTooltip
                                                                variant={"error"}
                                                                content={
                                                                    rowStatusMessages.map((message, i) => (
                                                                        <div key={i}>{message}</div>
                                                                    ))
                                                                }
                                                            />
                                                        )
                                                    }
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr className={"individual-coefficients__table__totals"}>
                                        <td colSpan={3}>Итого:</td>
                                        <td className={"individual-coefficients__table__cell--center"}>
                                            {formatNumber(totals.baseBonusAmount)}
                                        </td>
                                        <td className={"individual-coefficients__table__cell--center"}>
                                            {formatNumber(totals.motivationAmount)}
                                        </td>
                                        <td className={"individual-coefficients__table__cell--center"}>
                                            {formatNumber(totals.calculatedAmount)}
                                        </td>
                                        <td className={"individual-coefficients__table__cell--center"}>—</td>
                                        <td className={"individual-coefficients__table__status"}></td>
                                    </tr>
                                </tfoot>
                            </table>

                            {
                                totalsErrors.length > 0 && (
                                    <div className={"individual-coefficients__table__totals-error"}>
                                        {totalsErrors.map((message, i) => <div key={i}>{message}</div>)}
                                    </div>
                                )
                            }

                            <div className={"individual-coefficients__legend"}>
                                <div className={"individual-coefficients__legend__title"}>
                                    <span className={"individual-coefficients__mode-info__icon"}>i</span>
                                            Как выполняется расчет
                                </div>
                                <div className={"individual-coefficients__legend__columns"}>
                                    <div className={"individual-coefficients__legend__column"}>
                                        <div className={"individual-coefficients__legend__column__formula"}>
                                                    Расчетная сумма к начислению =
                                        </div>
                                        <div>Базовая сумма премии + Мотивация</div>
                                    </div>
                                    <div className={"individual-coefficients__legend__column"}>
                                        <div className={"individual-coefficients__legend__column__formula"}>
                                                    Коэффициент индивидуальной оценки (KI) =
                                        </div>
                                        <div>Расчетная сумма к начислению / Базовая сумма премии</div>
                                    </div>
                                    <div className={"individual-coefficients__legend__column"}>
                                        <div className={"individual-coefficients__legend__column__formula"}>
                                            <div style={{alignSelf: "flex-start"}}>
                                                      Допустимый диапазон KI
                                            </div>
                                            <IndividualCoefficientsTooltip
                                                content={"Коэффициент индивидуальной оценки должен " +
                                                            "находиться в диапазоне от 0 до 1,5."}
                                            />
                                        </div>
                                        <div style={{alignSelf: "flex-start"}}>от 0 до 1,5</div>
                                    </div>
                                </div>
                            </div>

                            <RequestButtons
                                references
                                backHandler={backHandler}
                                sendHandler={submitHandler}
                                isRunningSendHandler={sendButtonLoading}
                                disabled={!rows.length || hasUnfilledRows || totalsInvalid}
                            />

                            <NotificationWindowRequests
                                notificationWindowStatus={requestStatus}
                                setNotificationWindowStatus={setRequestStatus}
                                notificationWindowStyle={Boolean(requestStatus)}
                                blockingMessages
                            />

                            <Modal shown={modalStatus} close={setModalStatus}>
                                <BackHandlerModal
                                    handler={saveHandler}
                                    close={setModalStatus}
                                />
                            </Modal>

                            <Modal shown={modalSuccessStatus} close={setModalSuccessStatus}>
                                <SaveSuccessModal
                                    route={"/main/hr-services/requests/leader"}
                                    close={setModalSuccessStatus}
                                />
                            </Modal>

                            <ModalBanOverlay shown={modalSentStatus} close={setModalSentStatus}>
                                <RequestVerification
                                    individualCoefficients={{ uid, version, requestTypeName }}
                                    requestLink={"/documents?tab=4"}
                                    onValidation={onValidation}
                                    setOnValidation={setOnValidation}
                                    setModalError={setModalError}
                                    sendButtonLoading={sendButtonLoading}
                                    route={"/documents?tab=4"}
                                    setModalSentStatus={setModalSentStatus}
                                />
                            </ModalBanOverlay>

                            {
                                modalError.status && (
                                    <ModalBanOverlay shown={modalError} close={setModalError}>
                                        <Error500Modal
                                            modalError={modalError}
                                            setModalError={setModalError}
                                            function={closeErrorModal}
                                        />
                                    </ModalBanOverlay>
                                )
                            }
                        </>
                    )
                }
            </div>
                )

        </>
    );
};

export default IndividualCoefficients;
